import { throttle } from 'lodash';
import * as pdfjsLib from 'pdfjs-dist';
import React, { useEffect, useRef, useState } from 'react';
import { fetchErrorData } from '../../../../modules/API/API';
import { createTaskQueue } from '../../../../modules/util/helpers';
import { getFitSize } from '../../functions';
import { useGlobalState } from '../../../app/GlobalStateProvider';
// let pdfjsLib;
// (async function () {
//     pdfjsLib = await import('pdfjs-dist');
//     // const pdfjsWorker = await import('/libs/pdf.worker.min.mjs');
// })();
pdfjsLib.GlobalWorkerOptions.workerSrc = '/libs/pdf.worker.min.mjs';
const _replaceDrawLayoutQueue = createTaskQueue();
const PDFViewer = React.memo(({ index, documentPage, isWidthFit, scale, fullCanvas, canvasPos, setDrawCanvas, }) => {
    const { isDesktop, isTablet, isMobile } = useGlobalState();
    const pdfElement = useRef(null);
    const [load, setLoad] = useState(true);
    const [pdf, setPdf] = useState({
        document: undefined,
        id: undefined,
    });
    const [lastDrawImg, setLastImg] = useState({ style: { width: 0, height: 0 }, canvas: undefined });
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                if (pdf.document && fullCanvas.width > 0 && fullCanvas.height > 0) {
                    debounceDraw();
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [pdf, documentPage, index, fullCanvas]);
    const loadDocument = () => {
        return new Promise(async (resolve, reject) => {
            const drawList = APP.canvasList.get(documentPage);
            if (drawList && drawList.pdf) {
                resolve(drawList.pdf);
            }
            else {
                try {
                    const loadingTask = await pdfjsLib.getDocument({
                        url: `/docconv/get/${documentPage}`,
                        cMapUrl: '/cmaps/',
                        cMapPacked: true,
                    });
                    const newPdf = await loadingTask.promise;
                    APP.canvasList.set(documentPage, { pdf: newPdf });
                    resolve(newPdf);
                }
                catch (err) {
                    reject(err);
                }
            }
        });
    };
    useEffect(() => {
        setPdf({ document: undefined, id: undefined });
        _replaceDrawLayoutQueue.clear();
        loadDocument()
            .then(pdf => {
            setPdf({ document: pdf, id: documentPage });
        })
            .catch(err => {
            console.log(err);
        });
    }, [documentPage]);
    useEffect(() => {
        if (!pdf.document)
            return;
        _replaceDrawLayoutQueue.enqueue(async (onFinish) => {
            setLoad(true);
            if (documentPage !== pdf.id) {
                onFinish();
                return;
            }
            await drawImage(pdf.document, documentPage, index);
            setLoad(false);
            onFinish();
        });
    }, [pdf, index]);
    const debounceDraw = throttle(() => {
        if (pdf.document && fullCanvas.width > 0 && fullCanvas.height > 0) {
            drawImage(pdf.document, documentPage, index);
        }
    }, 1000);
    useEffect(() => {
        if (!pdf.document)
            return;
        if (fullCanvas.width === 0 || fullCanvas.height === 0)
            return;
        debounceDraw();
    }, [load, fullCanvas, scale, canvasPos, isWidthFit]);
    const drawImage = async (pdf, page, selectNum) => {
        try {
            const drawList = APP.canvasList.get(page);
            // // 캐시 확인
            // if (drawList && drawList[selectNum]) {
            // 	const item = drawList[selectNum];
            // 	console.log("drawList", item, page)
            // 	drawPdfCanvas(item.style, item.canvas);
            // 	return true;
            // }
            // 2. PDF.js 렌더링 (백그라운드)
            const pdfPage = await pdf.getPage(selectNum + 1);
            let imageStyle = {
                width: pdfPage.view[2],
                height: pdfPage.view[3],
            };
            const qualityBoost = 1;
            const dpr = !isMobile ? (window.devicePixelRatio || 1) * qualityBoost : 1;
            const drawStyle = getFitSize(fullCanvas, imageStyle, isWidthFit);
            const displayWidth = drawStyle.width;
            const displayHeight = drawStyle.height;
            // const minScale = 1;
            // const maxPixels = 3145728; // 최대 12MB (3M 픽셀 기준으로 가정, 각 픽셀 4바이트)
            let renderScale = dpr;
            if (displayHeight < 1000) {
                renderScale = Math.max(dpr * (1000 / displayHeight), dpr);
            }
            const renderWidth = Math.floor(imageStyle.width * renderScale);
            const renderHeight = Math.floor(imageStyle.height * renderScale);
            // 1. 빠른 이미지 로드 (프리뷰)
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            const image = new Image();
            const imgUrl = `/docconv/get/${page}/tn/${selectNum + 1}`;
            // 1-1. 프리뷰 이미지 로드
            await new Promise((resolve, reject) => {
                image.onload = () => {
                    canvas.width = imageStyle.width;
                    canvas.height = imageStyle.height;
                    // @ts-ignore
                    context.drawImage(image, 0, 0, canvas.width, canvas.height);
                    // 프리뷰를 사용자에게 즉시 표시
                    drawPdfCanvas({ width: image.width, height: image.height }, canvas);
                    setLoad(false);
                    resolve(true);
                };
                image.onerror = (err) => {
                    fetchErrorData({
                        message: 'document PDF timed outed',
                        data: {
                            error: '썸네일 로드 실패'
                        },
                    });
                    reject(err);
                };
                image.src = imgUrl;
            });
            const highResCanvas = document.createElement('canvas');
            const highResContext = highResCanvas.getContext('2d', {
                alpha: false,
                willReadFrequently: true,
                desynchronized: true,
            });
            highResCanvas.width = renderWidth;
            highResCanvas.height = renderHeight;
            highResCanvas.style.width = displayWidth + 'px';
            highResCanvas.style.height = displayHeight + 'px';
            const viewport = pdfPage.getViewport({ scale: renderScale });
            // 렌더링 작업 시작 (20초 제한 추가)
            const renderTask = pdfPage.render({
                canvasContext: highResContext,
                viewport,
            });
            const timeoutMs = 30000;
            let timeoutId;
            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = setTimeout(() => {
                    fetchErrorData({
                        message: 'document PDF timed outed',
                        data: { error: '저화질(섬네일) 이미지로 표출됨' },
                    });
                    renderTask.cancel();
                    reject(new Error("Render task timed out"));
                }, timeoutMs);
            });
            await Promise.race([renderTask.promise, timeoutPromise])
                .then(() => {
                // 렌더링된 고해상도 캔버스를 캐시 및 화면에 표시
                APP.canvasList.set(page, {
                    ...drawList,
                    [selectNum]: { style: imageStyle, canvas: highResCanvas },
                });
                drawPdfCanvas(imageStyle, highResCanvas);
                clearTimeout(timeoutId);
            })
                .catch((err) => {
                renderTask.cancel();
            });
            return true;
        }
        catch (err) {
            fetchErrorData({
                message: 'document PDF Load Error',
                data: err,
            });
            return false;
        }
    };
    /**
     * 캔버스 파일에 그리기
     * @param {object} imageStyle
     * @param {*} canvas
     * @returns
     */
    const drawPdfCanvas = (imageStyle, canvas) => {
        if (pdfElement.current) {
            const context = pdfElement.current.getContext('2d', {
                willReadFrequently: true,
            });
            if (context) {
                const drawStyle = getFitSize(fullCanvas, imageStyle, isWidthFit);
                const dpr = window.devicePixelRatio || 1;
                pdfElement.current.width = drawStyle.width * scale * dpr;
                pdfElement.current.height = drawStyle.height * scale * dpr;
                pdfElement.current.style.width = `${drawStyle.width * scale}px`;
                pdfElement.current.style.height = `${drawStyle.height * scale}px`;
                context.setTransform(1, 0, 0, 1, 0, 0);
                context.scale(dpr, dpr);
                context.fillStyle = '#fff';
                context.fillRect(canvasPos.x, canvasPos.y, drawStyle.width * scale, drawStyle.height * scale);
                context.drawImage(canvas, canvasPos.x, canvasPos.y, drawStyle.width * scale, drawStyle.height * scale);
                setDrawCanvas({
                    width: drawStyle.width,
                    height: drawStyle.height,
                });
                setLastImg({ style: imageStyle, canvas });
            }
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("canvas", { id: "pdf", className: "pdf", ref: pdfElement }),
        load && (React.createElement("div", { className: "load_cover nonSelect", style: { background: 'rgba(0,0,0, 0.5)' } },
            React.createElement("span", { className: "center", style: {
                    background: 'transparent',
                    fontSize: '30px',
                    left: '50%',
                } }, "Loading...")))));
});
export default PDFViewer;
